import {
  WorkflowV2EdgeDto,
  WorkflowV2NodeDto,
} from "@babackapp/private-api-client/types";
import {
  FloatingPanel,
  FloatingPanelContent,
  FloatingPanelHeader,
  FloatingPanelSeparator,
} from "@babackapp/ui/components/floating-panel";
import { cn } from "@babackapp/ui/lib/utils";
import { useTranslation } from "react-i18next";
import { ArrowRightFromLine, Plus } from "lucide-react";
import { useAddNodeContext } from "../context/add-node-context";
import { useNodeSelection } from "../context/node-selection-context";
import { useWorkflowForm } from "../context/workflows-editor-workflows-context";
import { useNodeById } from "../hooks/use-node-by-id";
import useNodePalette from "../hooks/use-node-palette";
import { EditorNodeType, workflowNodesLibrary } from "../nodes";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { useWorkflowsEditorConfig } from "../context/workflows-editor-config-context";
import { useMemo } from "react";

export function AddNodePanel() {
  const { sourceNodeId, sourceHandleId } = useAddNodeContext();
  const { nodeConfig } = useNodeById(sourceNodeId || "");
  const { t } = useTranslation("trackoffice");
  const { handleAdd } = useAddNode();
  const { data: shop } = useCurrentShopSuspense();
  const { availableActionTypes, domain } = useWorkflowsEditorConfig();

  const addableNodeTypes: EditorNodeType[] = useMemo(() => {
    const baseNodes: EditorNodeType[] = [
      "condition",
      "action:send_tracking_email",
      "action:update_web",
    ];

    const isWalletEnabled =
      shop.trackingPreferences?.isGoogleWalletEnabled ||
      shop.trackingPreferences?.isAppleWalletEnabled;

    if (isWalletEnabled) {
      baseNodes.push("action:send_wallet_notification");
    }

    // Mirrors the backends, which skip these actions without error when the
    // connection is incomplete. apiKey is redacted for non-admin members, so
    // email stands in for the credentials.
    const gorgias = shop.integrations?.gorgias;
    const gorgiasTracking = shop.trackingPreferences?.gorgiasTracking;
    const hasGorgiasApiKeyConnection =
      !!gorgias?.activated && !!gorgias.email && !!gorgias.domainUrl;
    // The Gorgias Tracking app is only read by the tracking engine.
    const hasGorgiasTrackingApp =
      domain === "tracking" &&
      !!gorgiasTracking?.activated &&
      !!gorgiasTracking.oauthAccount;
    if (hasGorgiasApiKeyConnection || hasGorgiasTrackingApp) {
      baseNodes.push("action:create_ticket_on_gorgias");
    }

    const zendesk = shop.integrations?.zendesk;
    // Zendesk OAuth is only supported by the tracking engine.
    const hasZendeskCredentials =
      !!zendesk?.email || (domain === "tracking" && !!zendesk?.oauthConnected);
    if (zendesk?.activated && zendesk.subDomain && hasZendeskCredentials) {
      baseNodes.push("action:create_ticket_on_zendesk");
    }

    baseNodes.push("action:modify_order_tags");
    baseNodes.push("action:send_webhook");

    const isKlaviyoEnabled = shop.integrations?.klaviyo?.activated;
    if (isKlaviyoEnabled) {
      baseNodes.push("action:send_klaviyo_event");
    }

    // Return domain actions — filtered out for tracking below.
    baseNodes.push("action:send_return_email");
    baseNodes.push("action:set_order_note");
    baseNodes.push("action:set_order_attribute");
    baseNodes.push("action:auto_treat_return");

    // The domain config bounds the palette: only actions the current domain
    // offers survive the shop-plan gating above.
    return baseNodes.filter(
      (type) => type === "condition" || availableActionTypes.includes(type),
    );
  }, [
    shop.trackingPreferences?.isGoogleWalletEnabled,
    shop.trackingPreferences?.isAppleWalletEnabled,
    shop.integrations?.gorgias,
    shop.trackingPreferences?.gorgiasTracking,
    shop.integrations?.zendesk,
    shop.integrations?.klaviyo?.activated,
    availableActionTypes,
    domain,
  ]);

  return (
    <FloatingPanel>
      <FloatingPanelHeader className="">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center whitespace-nowrap">
            <Plus className="mr-2" size={16} />
            {t("workflowsEditor.actions.addNode", "Add a new Node")}
          </div>
          <div
            className="text-xs text-muted-foreground flex items-center gap-1 whitespace-nowrap"
            style={{ fontSize: "0.7rem" }}
          >
            {t(nodeConfig?.config.name || "", { defaultValue: nodeConfig?.config.name })}{" "}
            {(sourceHandleId ?? "default") !== "default" &&
              ` (${sourceHandleId})`}
            <ArrowRightFromLine size={12} />
          </div>
        </div>
      </FloatingPanelHeader>
      <FloatingPanelSeparator />
      <FloatingPanelContent>
        <p className="text-sm text-muted-foreground">
          {t("workflowsEditor.actions.addNodeAlt", "What come's next?")}
        </p>
        <div className="flex flex-col gap-3">
          {addableNodeTypes.map((type) => {
            return (
              <AddNodeOption type={type} key={type} onClick={handleAdd(type)} />
            );
          })}
        </div>
      </FloatingPanelContent>
    </FloatingPanel>
  );
}

function AddNodeOption({
  type,
  onClick,
}: {
  type: EditorNodeType;
  onClick: () => void;
}) {
  const { config } = workflowNodesLibrary[type]!;
  const { classesWithHover } = useNodePalette(type);
  const { t } = useTranslation("trackoffice");

  return (
    <div
      className={cn(
        "flex flex-col shadow-md rounded-md bg-background border cursor-pointer",
      )}
      onClick={onClick}
    >
      <div
        className={cn(
          "flex border-b bg-default/5 text-default p-2",
          classesWithHover,
        )}
      >
        <config.Icon className="size-4 mr-2" />
        <span className="text-xs font-semibold flex-1">
          {t(config.name, config.name)}
        </span>
      </div>
      <div className="text-sm opacity-80 p-2">
        {t(config.description, config.description)}
      </div>
    </div>
  );
}

function useAddNode() {
  const { sourceNodeId, sourceHandleId, clearAddNodeSource } =
    useAddNodeContext();
  const form = useWorkflowForm();
  const { setSelected } = useNodeSelection();

  const handleAdd = (editorNodeType: EditorNodeType) => () => {
    if (!sourceNodeId) return;

    const workflow = form.getValues("workflow");
    if (!workflow) return;

    const selectedNodeConfig = workflowNodesLibrary[editorNodeType];
    if (!selectedNodeConfig?.config.newNodeDefaultData) return;

    const newNodeId = crypto.randomUUID();

    let apiNodeType: "action" | "condition";
    if (editorNodeType === "condition") {
      apiNodeType = "condition";
    } else {
      apiNodeType = "action";
    }

    const newNode: WorkflowV2NodeDto = {
      id: newNodeId,
      type: apiNodeType,
      data: selectedNodeConfig.config
        .newNodeDefaultData as WorkflowV2NodeDto["data"],
    };
    const newEdge: WorkflowV2EdgeDto = {
      id: crypto.randomUUID(),
      source: sourceNodeId,
      target: newNodeId,
      sourceHandle:
        sourceHandleId === "default" ? undefined : sourceHandleId || undefined,
      targetHandle: undefined,
      animated: false,
    };

    const updatedEdges = [...(workflow.definition.edges || []), newEdge];

    // Sort edges: true edges first, then false, then others
    // This helps dagre layout with proper ordering (prevent edges from crossing)
    const sortedEdges = updatedEdges.sort((a, b) => {
      if (a.sourceHandle === "true" && b.sourceHandle !== "true") return -1;
      if (a.sourceHandle !== "true" && b.sourceHandle === "true") return 1;
      if (a.sourceHandle === "false" && b.sourceHandle !== "false") return 1;
      if (a.sourceHandle !== "false" && b.sourceHandle === "false") return -1;
      return 0;
    });

    const trueTargets = new Set(
      sortedEdges.filter((e) => e.sourceHandle === "true").map((e) => e.target),
    );
    const falseTargets = new Set(
      sortedEdges
        .filter((e) => e.sourceHandle === "false")
        .map((e) => e.target),
    );

    const updatedNodes = [...(workflow.definition.nodes || []), newNode];
    const sortedNodes = updatedNodes.sort((a, b) => {
      const aIsTrue = trueTargets.has(a.id);
      const aIsFalse = falseTargets.has(a.id);
      const bIsTrue = trueTargets.has(b.id);
      const bIsFalse = falseTargets.has(b.id);

      if (aIsTrue && !bIsTrue) return -1;
      if (!aIsTrue && bIsTrue) return 1;
      if (aIsFalse && !bIsFalse) return 1;
      if (!aIsFalse && bIsFalse) return -1;
      return 0;
    });

    // Update the entire workflow object to ensure watchers trigger
    const updatedWorkflow = {
      ...workflow,
      definition: {
        ...workflow.definition,
        nodes: sortedNodes,
        edges: sortedEdges,
      },
    };

    form.setValue("workflow", updatedWorkflow, {
      shouldDirty: true,
    });

    clearAddNodeSource();
    setSelected({
      id: newNode.id,
      type: editorNodeType,
      data: newNode.data,
    });
  };

  return { handleAdd };
}
